/**
 * The options screen's rows (spec 08 §4, 07 §4): every field of `Settings` but the keys, each with
 * how it reads and what left and right do to it. A change is a `SettingsPatch` for the store; the
 * store clamps it again, so these only have to step sensibly.
 */

import { DEFAULT_SETTINGS, type MusicMode, type Settings, type SettingsPatch } from '../../settings';

/** How a row shows its value. */
export interface OptionView {
  value: string;
  /** 0..1: drawn as a bar before the value (the volumes, the palette lock). */
  bar?: number;
  /** Shown dim, and left/right do nothing (the palette lock in the 1987 look). */
  disabled?: boolean;
  /** The value is a warning (PRACTICE speeds). */
  warn?: boolean;
}

export interface OptionRow {
  id: string;
  label: string;
  /** One line shown under the list while the row is chosen. */
  help: (s: Settings) => string;
  view: (s: Settings) => OptionView;
  /** The change for one step left (-1) or right (+1); null when nothing changes. */
  step: (s: Settings, dir: -1 | 1) => SettingsPatch | null;
}

/** Steps a number on a grid of `by`, clamped to lo..hi, rounded so 0.1 + 0.2 stays 0.3. */
export function stepNumber(v: number, dir: -1 | 1, by: number, lo: number, hi: number): number {
  const next = Math.round(v / by + dir) * by;
  return Math.round(Math.min(hi, Math.max(lo, next)) * 1000) / 1000;
}

/** The next of `values` after `v`, round and round. */
function cycle<T>(values: readonly T[], v: T, dir: -1 | 1): T {
  const i = values.indexOf(v);
  const n = values.length;
  return values[(((i < 0 ? 0 : i) + dir) % n + n) % n] ?? v;
}

const pct = (v: number): string => `${Math.round(v * 100)} %`;
const onOff = (v: boolean): string => (v ? 'ON' : 'OFF');

const MUSIC: readonly MusicMode[] = ['remastered', '1987', 'off'];
const MUSIC_NAMES: Readonly<Record<MusicMode, string>> = { remastered: 'REMASTERED', '1987': '1987', off: 'OFF' };
const PIXELS = [1, 2, 3] as const;
const PIXEL_NAMES = ['SHARP', '2X', '3X'] as const;

/** A number that changed, or null if the clamp held it. */
const changed = <T>(before: T, after: T, patch: SettingsPatch): SettingsPatch | null =>
  (before === after ? null : patch);

export const OPTION_ROWS: readonly OptionRow[] = [
  {
    id: 'music', label: 'MUSIC',
    help: () => 'THE C64 TUNES: REMASTERED, OR AS THE SID PLAYED THEM',
    view: (s) => ({ value: MUSIC_NAMES[s.audio.music] }),
    step: (s, dir) => ({ audio: { music: cycle(MUSIC, s.audio.music, dir) } }),
  },
  {
    id: 'musicVolume', label: 'MUSIC VOLUME',
    help: () => 'LEFT AND RIGHT: QUIETER, LOUDER',
    view: (s) => ({ value: pct(s.audio.musicVolume), bar: s.audio.musicVolume }),
    step: (s, dir) => {
      const v = stepNumber(s.audio.musicVolume, dir, 0.1, 0, 1);
      return changed(s.audio.musicVolume, v, { audio: { musicVolume: v } });
    },
  },
  {
    id: 'effectsVolume', label: 'EFFECTS VOLUME',
    help: () => 'LEFT AND RIGHT: QUIETER, LOUDER',
    view: (s) => ({ value: pct(s.audio.effectsVolume), bar: s.audio.effectsVolume }),
    step: (s, dir) => {
      const v = stepNumber(s.audio.effectsVolume, dir, 0.1, 0, 1);
      return changed(s.audio.effectsVolume, v, { audio: { effectsVolume: v } });
    },
  },
  {
    id: 'extraEffects', label: 'EXTRA EFFECTS',
    help: () => 'SOUNDS THE C64 DID NOT HAVE: BLOW, POP, DEATH ...',
    view: (s) => ({ value: onOff(s.audio.extraEffects) }),
    step: (s) => ({ audio: { extraEffects: !s.audio.extraEffects } }),
  },
  {
    id: 'look', label: 'LOOK',
    help: () => '1987: THE C64\'S SIXTEEN COLOURS, FULL DITHER',
    view: (s) => ({ value: s.display.look === 'modern' ? 'MODERN' : '1987' }),
    step: (s) => ({ display: { look: s.display.look === 'modern' ? '1987' : 'modern' } }),
  },
  {
    id: 'crt', label: 'CRT',
    help: () => 'SCANLINES AND A DARKER EDGE',
    view: (s) => ({ value: onOff(s.display.crt) }),
    step: (s) => ({ display: { crt: !s.display.crt } }),
  },
  {
    id: 'paletteLock', label: 'PALETTE LOCK',
    help: (s) => (s.display.look === '1987' ? 'THE 1987 LOOK IS ALWAYS FULLY LOCKED'
      : 'HOW HARD THE PICTURE KEEPS TO THE PALETTE'),
    view: (s) => (s.display.look === '1987' ? { value: 'FULL', bar: 1, disabled: true }
      : { value: pct(s.display.paletteLock), bar: s.display.paletteLock }),
    step: (s, dir) => {
      if (s.display.look === '1987') return null;
      const v = stepNumber(s.display.paletteLock, dir, 0.05, 0, 1);
      return changed(s.display.paletteLock, v, { display: { paletteLock: v } });
    },
  },
  {
    id: 'pixelSize', label: 'PIXELS',
    help: () => 'SHARP, OR THE CHUNKY PIXEL LOOK',
    view: (s) => ({ value: PIXEL_NAMES[s.display.pixelSize - 1] ?? 'SHARP' }),
    step: (s, dir) => ({ display: { pixelSize: cycle(PIXELS, s.display.pixelSize, dir) } }),
  },
  {
    id: 'colourAid', label: 'COLOUR AID',
    help: () => 'ANGRY MONSTERS AND OLD BUBBLES SHOWN BY SHAPE TOO',
    view: (s) => ({ value: onOff(s.display.colourAid) }),
    step: (s) => ({ display: { colourAid: !s.display.colourAid } }),
  },
  {
    id: 'reduceFlashing', label: 'REDUCE FLASHING',
    help: () => 'SOFTER LIGHTNING, BURSTS AND FLASHES',
    view: (s) => ({ value: onOff(s.display.reduceFlashing) }),
    step: (s) => ({ display: { reduceFlashing: !s.display.reduceFlashing } }),
  },
  {
    id: 'gameSpeed', label: 'GAME SPEED',
    help: () => 'BELOW 100 % IS PRACTICE: THE HIGH SCORE IS NOT KEPT',
    view: (s) => (s.play.gameSpeed < 1 ? { value: `${pct(s.play.gameSpeed)} PRACTICE`, warn: true }
      : { value: pct(s.play.gameSpeed) }),
    step: (s, dir) => {
      const v = stepNumber(s.play.gameSpeed, dir, 0.1, 0.5, 1);
      return changed(s.play.gameSpeed, v, { play: { gameSpeed: v } });
    },
  },
];

/** Everything on the options screen back to the faithful defaults; the keys have their own reset. */
export const OPTIONS_DEFAULTS: SettingsPatch = {
  audio: DEFAULT_SETTINGS.audio, display: DEFAULT_SETTINGS.display, play: DEFAULT_SETTINGS.play,
};
